import { useCallback, useEffect, useState } from "react";
import { Money } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API, formatCurrency, fmtErr, today, PAYMENT_MODES } from "../lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function RecordPayment({ type, onSaved, preset, open: controlledOpen, onOpenChange }) {
  const isSale = type === "sale";
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [parties, setParties] = useState([]);
  const [docs, setDocs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [partyId, setPartyId] = useState("");
  const [docId, setDocId] = useState("");
  const [amount, setAmount] = useState("");
  const [transactionNo, setTransactionNo] = useState("");
  const [exchangeRate, setExchangeRate] = useState("1");
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState("Cash");
  const [accountId, setAccountId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    try {
      const [p, d, a] = await Promise.all([
        API.get(`/parties?type=${isSale ? "customer" : "vendor"}`),
        API.get(isSale ? "/sales" : "/purchases"),
        API.get("/banking/accounts"),
      ]);
      setParties(p.data);
      setDocs(d.data.filter((x) => x.status !== "paid"));
      setAccounts(a.data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  }, [open, isSale]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (open && preset) {
      setPartyId(preset.partyId || "");
      setDocId(preset.docId || "");
      setAmount(preset.amount != null ? String(preset.amount) : "");
      setExchangeRate("1");
    }
  }, [open, preset]);

  const openDocs = docs.filter((d) => !partyId || d.party_id === partyId);
  const selectedDoc = docs.find((d) => d.id === docId);
  const due = selectedDoc ? Math.max(0, selectedDoc.total - (selectedDoc.tds_amount || 0) - selectedDoc.paid_amount) : 0;
  const currency = selectedDoc?.currency || "INR";
  const documentRate = Number(selectedDoc?.exchange_rate) > 0 ? Number(selectedDoc.exchange_rate) : 1;
  const displayDue = due / documentRate;

  const submit = async () => {
    if (!docId) return toast.error(`Select an ${isSale ? "invoice" : "bill"}`);
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    if (currency !== "INR" && (!Number.isFinite(Number(exchangeRate)) || Number(exchangeRate) <= 0)) return toast.error("Enter a valid INR exchange rate");
    setSaving(true);
    try {
      await API.post("/payments", {
        doc_id: docId,
        doc_type: type,
        amount: Number(amount),
        currency,
        exchange_rate: currency === "INR" ? 1 : Number(exchangeRate),
        transaction_no: transactionNo.trim(),
        date,
        account_id: accountId || null,
        mode,
      });
      toast.success(`Payment ${isSale ? "received" : "paid"} recorded`);
      setOpen(false);
      setPartyId("");
      setDocId("");
      setAmount("");
      setTransactionNo("");
      setExchangeRate("1");
      onSaved?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {controlledOpen === undefined && (
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={isSale ? "record-receipt-button" : "record-payment-paid-button"}>
          <Money size={16} className="mr-1" /> {isSale ? "Payment Received" : "Payment Paid"}
        </Button>
      </DialogTrigger>
      )}
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">
            {isSale ? "Record Payment Received" : "Record Payment Paid"}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Record a payment {isSale ? "received from a customer" : "paid to a vendor"} against an open {isSale ? "invoice" : "bill"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{isSale ? "Customer" : "Vendor"} (optional filter)</Label>
            <Select value={partyId} onValueChange={(v) => { setPartyId(v); setDocId(""); }}>
              <SelectTrigger data-testid="payment-party-select">
                <SelectValue placeholder="All parties" />
              </SelectTrigger>
              <SelectContent>
                {parties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{isSale ? "Invoice" : "Bill"}</Label>
            <Select value={docId} onValueChange={(value) => { setDocId(value); setExchangeRate("1"); }}>
              <SelectTrigger data-testid="payment-doc-select">
                <SelectValue placeholder="Select open document" />
              </SelectTrigger>
              <SelectContent>
                {openDocs.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.number} · {d.party_name} · due {formatCurrency((d.total - (d.tds_amount || 0) - d.paid_amount) / (Number(d.exchange_rate) || 1), d.currency || "INR")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {openDocs.length === 0 && (
              <p className="text-xs text-muted-foreground">No open {isSale ? "invoices" : "bills"} with a balance due.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Amount ({currency}){selectedDoc ? ` — due ${formatCurrency(displayDue, currency)}` : ""}</Label>
              <Input type="number" min="0" step="any" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="payment-form-amount-input" />
            </div>
            {currency !== "INR" && (
              <div className="space-y-2">
                <Label>Payment rate (1 {currency} in INR)</Label>
                <Input type="number" min="0" step="any" value={exchangeRate} onChange={(e) => setExchangeRate(e.target.value)} data-testid="payment-form-exchange-rate-input" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="payment-form-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="payment-form-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Bank account</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger data-testid="payment-form-account-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent>
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label>Transaction No. / Narration (optional)</Label>
              <Input maxLength={255} value={transactionNo} onChange={(e) => setTransactionNo(e.target.value)} data-testid="payment-form-transaction-no-input" />
              <p className="text-xs text-muted-foreground">Used to identify this payment in a later bank statement upload.</p>
            </div>
          </div>
          <Button className="w-full" onClick={submit} disabled={saving} data-testid="payment-form-submit-button">
            {saving ? "Recording…" : `Record ${isSale ? "Receipt" : "Payment"}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
